/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react/destructuring-assignment */
import React, { useContext, useEffect, useState } from 'react';
import { Button, Checkbox, Popover, Switch, Tag, Typography } from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import { useWatch } from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import { ILoan } from '../closingCostsDetails.types';
import { useContextReports } from '../../../../../context/reports';
import { useContextNotifications } from '../../../../../context/notifications';
import { EnumReportTypes, ILoanValue, IReportRow } from '../../../../../types/reports';
import { DragHandleContext } from '../LoanClosingCostSortableRow';
import { useReportClosingCostActiveUpdate, useReportLoanUpdate, } from '../../../../../hooks/reports';
import { ISettingItem } from '../../../../../hooks/users';
import { calculateAPR } from '../../../../../utils/calculateAPR';
import { prepareLoanDataFromForm } from '../../../../../utils/prepareData';
import styles from '../../Sections/LoanComparisons/index.module.less';
import UpdateClosingCost from '../UpdateClosingCost';
import LoanClosingCost from '../LoanClosingCost';
import { calculateLoanClosingCostAmount } from '../../../../../utils/calculateLoanClosingCostAmount';
import { EnumClosingCostType } from '../../../../../types/settings';
import { calculateClosingCosts } from '../../../../../utils/calculateClosingCosts';
import { calculateLoanAmountWithUip } from '../../../../../utils/calculateLoanAmountWithUip';
import { calculateDownPaymentPrice } from '../../../../../utils/calculateDownPaymentPrice';
import { calculatePrincipalInterest } from '../../../../../utils/calculatePrincipalInterest';
import { formatAmount } from '../../../../../utils/formatAmount';
import RemoveClosingCostsCol from '../RemoveClosingCostsCol';

const nonMutableColumnsMapping = {
  loanPoints: 'Loan Points',
  uip: 'Upfront Mtg Insurance Premium',
  vaFundingFee: 'VA Funding Fee',
}

const nonMutableColumns = Object.values(nonMutableColumnsMapping);
const MAIN_COLS_WIDTH = 362;
const COLS_WIDTH = 230;

const AutoEstimatedTag = () => {
  const { isBorrower } = useContextReports();

  const colorBlue = getComputedStyle(document.documentElement).getPropertyValue('--color-blue');

  return (
    <div
      style={{
        marginTop: 'auto',
        marginLeft: 'auto',
      }}
    >
      {isBorrower ? (
        <Tag color={colorBlue} className="m-0 capitalize cursor-default">
          Needs Estimate
        </Tag>
      ) : (
        <Popover
          content={
            <div className={styles.popperContent}>
              <Typography
                className={styles.popperText}
                style={{
                  textAlign: 'center'
                }}
              >
                This column was added by your borrower.
              </Typography>
              <Typography
                className={styles.popperText}
                style={{
                  textAlign: 'center'
                }}
              >
                Create a duplicate column to provide a closing cost estimate.
              </Typography>
            </div>
          }
          trigger="hover"
        >
          <Tag color={colorBlue} className="m-0 capitalize cursor-default">
            Needs Estimate
          </Tag>{' '}
        </Popover>
      )}
    </div>
  )
}

const ColAddress = (
  address: string,
  name: string,
  isLoanTypeBorrower: boolean,
  loanIndex: number,
) => {
  let delimiter = '';

  if (address?.includes(';')) {
    delimiter = ';';
  } else if (address?.includes(',')) {
    delimiter = ',';
  }
  const address1 = delimiter ? address?.split(delimiter)[0] : address;
  const address2 = delimiter ? address?.replace(`${address1}${delimiter}`, '') : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', textTransform: 'uppercase', height: '97px' }}>
      <p style={{ margin: '0' }}>{name}</p>
      <p style={{ whiteSpace: 'normal', fontSize: '0.825rem', margin: '0' }}>{address1 ? `${address1}` : ''}</p>
      <p style={{ whiteSpace: 'normal', fontSize: '0.825rem', marginBottom: '4px' }}>{address2}</p>
      {!isLoanTypeBorrower && <RemoveClosingCostsCol loanIndex={loanIndex} />}
      {isLoanTypeBorrower && <AutoEstimatedTag />}
    </div>
  )
};

const DragHandle: React.FC = () => {
  const { listeners, setActivatorNodeRef } = useContext(DragHandleContext);

  return (
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'move' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
};

const useClosingCostsTable = () => {
  const form = useFormInstance();
  const loans: ILoan[] = useWatch('loans', form);
  const {
    isBorrower,
    reportId,
    reportLoanClosingCosts,
    reportClosingCosts,
    setReportClosingCosts,
    report,
    getReportClosingCosts,
    getReportLoanClosingCosts,
    setPreviewLoading
  } = useContextReports();

  const { openNotification } = useContextNotifications();
  const { fetch: fetchUpdateCsActive } = useReportClosingCostActiveUpdate();
  const tableView: Record<string, IReportRow> = useWatch('tableView', form);
  const [currentClosingCosts, setReportLoanClosingCosts] = useState<any[]>([]);
  const reportLoanUpdate = useReportLoanUpdate();

  useEffect(() => {
    if (reportLoanClosingCosts) {
      setReportLoanClosingCosts(reportLoanClosingCosts);
    }
  }, [reportLoanClosingCosts]);

  useEffect(() => {
    getReportClosingCosts?.()
  }, [])

  if (!reportLoanClosingCosts || !loans) return { columns: [], dataSource: [] };

  const calculateColumnTotals = (loanId: number): number => {
    const columnTotal = reportLoanClosingCosts
      ?.filter((item) => item.loanId === loanId)
      .reduce((total, cost) => {
        const loan = loans.find((l) => l.id === loanId);

        if (
          (cost.text === nonMutableColumnsMapping.uip && (!tableView.uip?.active || loan?.addUip))
          || (cost.text === nonMutableColumnsMapping.vaFundingFee &&
            (!tableView.vaFundingFee?.active || loan?.externalLoan?.addVaFundingFee))
          // || (cost.text === nonMutableColumnsMapping.loanPoints && !tableView.loanPoints?.active)
        ) {
          return total;
        }

        // @ts-ignore report id exists
        const reportClosingCost = reportClosingCosts?.find(c => c.name === cost.name && c.reportId === reportId);

        if (reportClosingCost?.active === false) {
          return total;
        }

        return total + (cost.amount || 0);
      }, 0)

    return columnTotal;
  };

  const refetchData = async () => {
    await getReportClosingCosts?.();
    await getReportLoanClosingCosts?.();
  };

  const getLoanDisplayName = (loan: ILoan, index: number, isRefinance: boolean): string => {
    if (loan?.name) return loan.name;
    if (isRefinance) return index === 0 ? "Current loan" : `Refi Option ${index}`;

    return `Option ${index + 1}`;
  };

  const updateClosingCost = async (
    checked: boolean,
    reportClosingCost: ISettingItem | undefined,
    type: 'active' | 'aprIncluded',
  ) => {
    try {
      if (!reportClosingCosts || !reportLoanClosingCosts) {
        return
      }

      setPreviewLoading(true);

      const updatedField = type === 'active' ? { active: checked } : { aprIncluded: checked };

      setReportClosingCosts?.(prev => {
        const updatedReportClosingCosts = prev?.map(item =>
          item?.id === reportClosingCost?.id ? { ...item, ...updatedField } : item)

        const isRefinance = report?.reportType === EnumReportTypes.refinance;

        loans.forEach((loan, index) => {
          const loanClosingCost = reportLoanClosingCosts.find(reportLoanClosingCost =>
            reportLoanClosingCost.name === reportClosingCost?.name && reportLoanClosingCost.loanId === loan.id);

          const loanIdx = loans.findIndex((l) => l.id === loan.id);

          const closingCostsTotal = form.getFieldValue(['loans', loanIdx, 'closingCosts', 'value']) ?? 0
          const isFirstItemInRefinance = isRefinance && loanIdx === 0;
          const downPayment = parseFloat(form.getFieldValue(['loans', loanIdx, 'downPayment', 'value'])) || 0;
          const removeDownPayment = form.getFieldValue(['loans', loanIdx, 'externalLoan', 'removeDownPayment']);
          const uipActive = form.getFieldValue(['tableView', 'uip', 'active']);
          const vaFundingFeeActive = form.getFieldValue(['tableView', 'vaFundingFee', 'active']);
          const loanPointsActive = form.getFieldValue(['tableView', 'loanPoints', 'active']);
          const loanAmount = parseFloat(form.getFieldValue(['loans', loanIdx, 'loanAmount', 'value'])) || 0;
          const interestRate = parseFloat(form.getFieldValue(['loans', loanIdx, 'interestRate', 'value'])) || 0;
          const loanPeriodInYears =
            parseFloat(form.getFieldValue(['loans', loanIdx, 'loanPeriodInYears', 'value'])) || 0;
          const closingCostsClearedAt = form.getFieldValue(['loans', loanIdx, 'externalLoan', 'closingCostsClearedAt']);

          const loanClosingCosts = reportLoanClosingCosts?.filter(cost => cost.loanId === loan.id);

          const updatedLoan = form.getFieldValue(['loans', loanIdx]);

          const totalAprRelatedClosingCost = loanClosingCosts?.reduce((total, closingCost) => {
            const updatedReportClosingCost = updatedReportClosingCosts?.find(c => c.name === closingCost.name);

            if (closingCost.name === 'uip' && uipActive) {
              return total + closingCost.amount
            }

            if (closingCost.name === 'vaFundingFee' && vaFundingFeeActive) {
              return total + closingCost.amount
            }

            if (closingCost.name === 'loan_points' && loanPointsActive) {
              return total + closingCost.amount
            }

            if (updatedReportClosingCost?.aprIncluded && updatedReportClosingCost?.active && !closingCostsClearedAt) {
              const closingCostAmount = closingCost.type === EnumClosingCostType.flatFee ?
                closingCost.amount : updatedReportClosingCost?.amount ?? 0;

              const amount = calculateLoanClosingCostAmount(
                updatedLoan,
                closingCost.type,
                closingCostAmount,
                closingCost.propertyTaxesMonthsCollected,
                closingCost.daysCollected
              )

              return total + amount;
            }

            return total
          }, 0) ?? 0

          const normalizedAprClosingCost = parseFloat(formatAmount(Math.max(0, totalAprRelatedClosingCost)));

          const apr = calculateAPR(loanAmount, interestRate, loanPeriodInYears, normalizedAprClosingCost);

          form.setFieldValue(['loans', loanIdx, 'apr', 'value'], apr);
          form.setFieldValue(['loans', loanIdx, 'aprRelatedClosingCost', 'value'], normalizedAprClosingCost);

          if (type === 'active' && !isFirstItemInRefinance) {
            if (checked) {
              const totalClosingCosts = parseFloat(closingCostsTotal) + (loanClosingCost?.amount || 0);

              const formatedClosingCosts = parseFloat(formatAmount(totalClosingCosts))

              const loanAmountWithUip = calculateLoanAmountWithUip(form, index, { closingCosts: formatedClosingCosts  })

              form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

              form.setFieldValue(['loans', loanIdx, 'closingCosts', 'value'], formatedClosingCosts);

              const downPaymentPrice = calculateDownPaymentPrice(form, loanIdx)

              form.setFieldValue(['loans', loanIdx, 'down_payment_price'], downPaymentPrice)

              // eslint-disable-next-line @typescript-eslint/no-shadow
              const updatedLoan = form.getFieldValue(['loans', loanIdx]);

              const principalInterest = calculatePrincipalInterest(updatedLoan);

              form.setFieldValue(['loans', loanIdx, 'principalInterest', 'value'], principalInterest);
            } else {
              const totalClosingCosts = (parseFloat(closingCostsTotal) - (loanClosingCost?.amount || 0));

              const formatedClosingCosts = parseFloat(formatAmount(totalClosingCosts))

              const loanAmountWithUip = calculateLoanAmountWithUip(form, index, { closingCosts: formatedClosingCosts  })

              form.setFieldValue(['loans', index, 'loanAmountWithUip', 'value'], loanAmountWithUip);

              form.setFieldValue(['loans', loanIdx, 'closingCosts', 'value'], formatedClosingCosts);

              const downPaymentPrice = calculateDownPaymentPrice(form, loanIdx)

              form.setFieldValue(['loans', loanIdx, 'down_payment_price'], downPaymentPrice)

              // eslint-disable-next-line @typescript-eslint/no-shadow
              const updatedLoan = form.getFieldValue(['loans', loanIdx]);

              const principalInterest = calculatePrincipalInterest(updatedLoan);

              form.setFieldValue(['loans', loanIdx, 'principalInterest', 'value'], principalInterest);
            }
          }
        });

        return updatedReportClosingCosts
      })

      await fetchUpdateCsActive(updatedField, reportClosingCost?.id);

      const updatedLoans: ILoanValue[] = form?.getFieldValue('loans')

      await reportLoanUpdate.fetch({ loans: prepareLoanDataFromForm(updatedLoans) }, reportId)
    } catch (error) {
      openNotification?.({
        message: 'Error',
        description: 'An error occurred while updating the closing cost amount',
        type: 'error',
      });
    } finally {
      setPreviewLoading(false);
    }
  }

  const columns = [
    {
      title: (
        <div style={{ height: '97px' }}>
          <p>Closing Costs</p>
        </div>
      ),
      dataIndex: 'text',
      key: 'text',
      width: MAIN_COLS_WIDTH,
      render: (text: string, record: any) => {
        const allLoans = Object.keys(record)
          .filter((key) => key.startsWith('loan_'))
          .map((key) => ({
            ...record[key],
            text: record.text,
          }));

        const loanClosingCost = reportLoanClosingCosts?.find(cost => cost.id === record.id);

        const reportClosingCost = reportClosingCosts?.find(cost => cost.name === loanClosingCost?.name
          // @ts-ignore report id exists here
          && cost.reportId === reportId);

        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '2px',
            }}
          >
            <Popover content={text}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {!nonMutableColumns.includes(text) && !isBorrower && <DragHandle />}
                <strong
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    margin: '0 8px' ,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: isBorrower ? "259px" : "240px"
                  }}
                >
                  {text}
                </strong>
              </div>
            </Popover>

            {!nonMutableColumns.includes(text) && (
              <div style={{ gap: '6px', display: 'flex', alignItems: 'center' }}>
                {!isBorrower && (
                  <>
                    <Switch
                      size="small"
                      checked={reportClosingCost?.active}
                      onChange={(checked) => updateClosingCost(checked, reportClosingCost, 'active')}
                    />
                    <Popover content="Apply to APR-Related Costs">
                      <Checkbox
                        checked={reportClosingCost?.aprIncluded}
                        disabled={!reportClosingCost?.active}
                        onChange={(event) => updateClosingCost(event.target.checked, reportClosingCost, 'aprIncluded')}
                      />
                    </Popover>
                    <UpdateClosingCost
                      reportId={reportId as string}
                      updateRows={allLoans}
                      refetchData={refetchData}
                      isActive={Boolean(reportClosingCost?.active)}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    ...loans?.map((loan: ILoan, index) => {
      if (loans?.[index]?.hidden) {
        return null;
      }

      const loanKey = `loan_${loan.id}`;
      const isRefinance = report?.reportType === EnumReportTypes.refinance;

      const name = getLoanDisplayName(loan, index, isRefinance);
      const address = loan?.address?.toUpperCase();
      const isLoanTypeBorrower = loan?.type === 'borrower';
      const isFirstItemInRefinance = isRefinance && index === 0;

      return {
        title: ColAddress(address, name, isLoanTypeBorrower, index),
        dataIndex: loanKey,
        key: loanKey,
        width: COLS_WIDTH,
        render: (value: any, record: any) => (
          <LoanClosingCost
            value={value}
            record={record}
            refetchData={refetchData}
            calculateColumnTotals={calculateColumnTotals}
            setReportLoanClosingCosts={setReportLoanClosingCosts}
            loan={loan}
            nonMutableColumnsMapping={nonMutableColumnsMapping}
            currentClosingCosts={currentClosingCosts}
            isFirstItemInRefinance={isFirstItemInRefinance}
            loanIndex={index}
          />
        ),
      };
    }).filter(Boolean) as {
      title: JSX.Element;
      dataIndex: string;
      key: string;
      width: number;
      render: (text: string, record: any) => JSX.Element;
    }[],
  ];

  const uniqueClosingCosts = new Map<string, any>();

  currentClosingCosts.forEach((closingCost) => {
    const costKey = `${closingCost.text}`;

    if (!uniqueClosingCosts.has(costKey)) {
      uniqueClosingCosts.set(costKey, {
        id: closingCost.id,
        text: closingCost.text,
        amounts: {},
      });
    }

    const costGroup = uniqueClosingCosts.get(costKey);

    costGroup.amounts[`loan_${String(closingCost.loanId)}`] = {
      amount: closingCost.amount,
      closingCostId: closingCost.id,
      type: closingCost.type,
      editable: closingCost.editable,
      propertyTaxesMonthsCollected: closingCost.propertyTaxesMonthsCollected,
      daysCollected: closingCost.daysCollected,
    };
  });

  const dataSource = Array.from(uniqueClosingCosts.values()).map(
    (cost, index) => ({
      key: index,
      id: cost.id,
      text: cost.text,
      ...Object.fromEntries(
        Object.entries(cost.amounts).map(([loanKey, value]) => [
          loanKey,
          value,
        ])
      ),
    })
  ).filter((record) => {
    if (
      (record.text === nonMutableColumnsMapping.uip && !tableView.uip?.active) ||
      (record.text === nonMutableColumnsMapping.loanPoints && !tableView.loanPoints?.active) ||
      (record.text === nonMutableColumnsMapping.vaFundingFee && !tableView.vaFundingFee?.active)
    ) {
      return false;
    }

    const reportClosingCost = reportClosingCosts?.find(c => c.text === record.text);

    return !(reportClosingCost && !reportClosingCost.active && isBorrower);
  });


  return { columns, dataSource, calculateColumnTotals };
};

export default useClosingCostsTable;
